import React, { useState, useEffect, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Grid,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Divider,
  Box,
  Snackbar,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Chip,
  Card,
  CardContent,
  CardActions,
  Tabs,
  Tab,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Alert, Rating } from '@material-ui/lab';

// Icons
import StorefrontIcon from '@material-ui/icons/Storefront';
import SaveIcon from '@material-ui/icons/Save';
import BusinessIcon from '@material-ui/icons/Business';
import PhoneIcon from '@material-ui/icons/Phone';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import SearchIcon from '@material-ui/icons/Search';
import AssignmentIndIcon from '@material-ui/icons/AssignmentInd';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import PaymentIcon from '@material-ui/icons/Payment';
import ReceiptIcon from '@material-ui/icons/Receipt';
import SendIcon from '@material-ui/icons/Send';
import ExtensionIcon from '@material-ui/icons/Extension';
import FeedbackIcon from '@material-ui/icons/Feedback';
import EmojiObjectsIcon from '@material-ui/icons/EmojiObjects';
import FlashOnIcon from '@material-ui/icons/FlashOn';
import CardMembershipIcon from '@material-ui/icons/CardMembership';
import LaunchIcon from '@material-ui/icons/Launch';
import ScheduleIcon from '@material-ui/icons/Schedule';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import CategoryIcon from '@material-ui/icons/Category';

import PageContent from '../../../components/Main/Content/mainContent';
import UIContainer from '../../../components/UI/Container/UIContainer';
import UIFormTitle from '../../../components/UI/Form/Title/UIFormTitle';
import UIImageUpload from '../../../components/UI/ImageUpload/UIImageUpload';
import StoreContext from '../../../components/Store/context';
import AccessRestricted from '../../../components/Error/AccessRestricted/AccessRestricted';
import { isAdminUser } from '../../../utils/permissionUtils';
import { auth, db } from '../../../firebase';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  onSnapshot,
  addDoc,
} from 'firebase/firestore';
import SubscriptionCheckoutModal from '../../../components/UI/Subscription/SubscriptionCheckoutModal';
import { getInvoiceUrlForCompany } from '../../../services/subscriptionService';
import axios from 'axios';
import { fetchCnpjInfo, fetchCepInfo } from '../../../utils/brasilApi';
import { getHubApiUrl, getHubAuthHeaders } from '../../../utils/hubApi';
import { useFirebaseAuth } from '../../../contexts/FirebaseAuthContext';

// Helper formatters
const formatCpfCnpj = (value) => {
  if (!value) return '';
  const clean = String(value).replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  const hasLetters = /[A-Z]/.test(clean);
  if (hasLetters || clean.length > 11) {
    const c = clean.slice(0, 14);
    const p1 = c.slice(0, 2);
    const p2 = c.slice(2, 5);
    const p3 = c.slice(5, 8);
    const p4 = c.slice(8, 12);
    const p5 = c.slice(12, 14);

    let formatted = p1;
    if (p2) formatted += '.' + p2;
    if (p3) formatted += '.' + p3;
    if (p4) formatted += '/' + p4;
    if (p5) formatted += '-' + p5;
    return formatted;
  } else {
    const c = clean.slice(0, 11);
    const p1 = c.slice(0, 3);
    const p2 = c.slice(3, 6);
    const p3 = c.slice(6, 9);
    const p4 = c.slice(9, 11);

    let formatted = p1;
    if (p2) formatted += '.' + p2;
    if (p3) formatted += '.' + p3;
    if (p4) formatted += '-' + p4;
    return formatted;
  }
};

const formatCep = (value) => {
  if (!value) return '';
  const clean = String(value).replace(/\D/g, '').slice(0, 8);
  if (clean.length > 5) {
    return `${clean.slice(0, 5)}-${clean.slice(5)}`;
  }
  return clean;
};

const formatPhone = (value) => {
  if (!value) return '';
  const clean = String(value).replace(/\D/g, '').slice(0, 11);
  if (clean.length > 10) {
    return `(${clean.slice(0, 2)}) ${clean.slice(2, 7)}-${clean.slice(7)}`;
  } else if (clean.length > 6) {
    return `(${clean.slice(0, 2)}) ${clean.slice(2, 6)}-${clean.slice(6)}`;
  } else if (clean.length > 2) {
    return `(${clean.slice(0, 2)}) ${clean.slice(2)}`;
  }
  return clean;
};

const useStyles = makeStyles((theme) => ({
  headerBox: {
    padding: '16px 20px',
    borderRadius: 12,
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    marginBottom: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 16,
    [theme.breakpoints.down('sm')]: {
      padding: '14px 14px',
    },
  },
  companyAvatar: {
    width: 54,
    height: 54,
    backgroundColor: '#eff6ff',
    color: '#17497F',
    borderRadius: 12,
    border: '1px solid #cbd5e1',
  },
  tabsWrapper: {
    borderBottom: '2px solid #e2e8f0',
    marginBottom: 20,
    '& .MuiTabs-indicator': {
      backgroundColor: '#17497F',
      height: 3,
      borderRadius: '3px 3px 0 0',
    },
  },
  tabButton: {
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '0.94rem',
    minHeight: 48,
    padding: '10px 20px',
    color: '#64748b',
    '&.Mui-selected': {
      color: '#17497F',
    },
  },
  formSectionBox: {
    padding: 20,
    borderRadius: 12,
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    height: '100%',
    [theme.breakpoints.down('sm')]: {
      padding: 14,
    },
  },
  planCard: {
    background: 'linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)',
    borderRadius: 14,
    padding: theme.spacing(2.5),
    border: '1.5px solid #bfdbfe',
    boxShadow: '0 4px 16px -2px rgba(23, 73, 127, 0.08)',
    position: 'relative',
    overflow: 'hidden',
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
  },
  addonCardActive: {
    backgroundColor: '#ffffff',
    borderRadius: 14,
    border: '1.5px solid #10b981',
    boxShadow: '0 2px 10px rgba(16, 185, 129, 0.08)',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 18px rgba(16, 185, 129, 0.16)',
    },
  },
  addonCardAvailable: {
    backgroundColor: '#ffffff',
    borderRadius: 14,
    border: '1px solid #cbd5e1',
    boxShadow: '0 2px 8px rgba(15, 23, 42, 0.04)',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    transition: 'transform 0.2s, border-color 0.2s',
    '&:hover': {
      borderColor: '#17497F',
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 18px rgba(23, 73, 127, 0.12)',
    },
  },
  feedbackContainer: {
    padding: 24,
    borderRadius: 14,
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    [theme.breakpoints.down('sm')]: {
      padding: 16,
    },
  },
}));

const ConfiguracaoEmpresa = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const isTablet = useMediaQuery(theme.breakpoints.up('sm'));
  const { credencials, setCredencials } = useContext(StoreContext);
  const { currentUser: activeUser } = useFirebaseAuth();

  // Tab State: 0: Dados da Empresa, 1: Plano & Add-ons, 2: Feedback & Sugestões
  const [currentTab, setCurrentTab] = useState(0);

  // Form State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cnpjLoading, setCnpjLoading] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [pendingInvoiceUrl, setPendingInvoiceUrl] = useState(null);
  const [loadingInvoice, setLoadingInvoice] = useState(false);

  // Company Details State
  const [accountId, setAccountId] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [pseudonym, setPseudonym] = useState('');
  const [segment, setSegment] = useState('Comércio Geral');
  const [cpfCnpj, setCpfCnpj] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [responsibleName, setResponsibleName] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [address, setAddress] = useState('');
  const [addressNumber, setAddressNumber] = useState('');
  const [complement, setComplement] = useState('');
  const [province, setProvince] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');

  // Sync segment changes from onboarding or other components
  useEffect(() => {
    const handleSegUpdate = (e) => {
      const updatedSeg = e?.detail?.segmentKey || e?.detail?.segment;
      if (updatedSeg) {
        setSegment(updatedSeg);
      }
    };
    window.addEventListener('duzz:onboarding_seg_updated', handleSegUpdate);
    return () => {
      window.removeEventListener('duzz:onboarding_seg_updated', handleSegUpdate);
    };
  }, []);

  // Subscriptions, Plans & Addons State
  const [accountDocData, setAccountDocData] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [addons, setAddons] = useState([]);
  const [toggleAddonLoading, setToggleAddonLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null,
  });

  // Feedback State
  const [feedbackCategory, setFeedbackCategory] = useState('SUGGESTION');
  const [feedbackRating, setFeedbackRating] = useState(5);
  const [feedbackTitle, setFeedbackTitle] = useState('');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [submittingFeedback, setSubmittingFeedback] = useState(false);

  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  // Access check (Admin only)
  const isAdmin = isAdminUser(credencials);

  // 1. Initial Company Data Fetch
  useEffect(() => {
    fetchCompanyData();
  }, [credencials, activeUser]);

  // 2. Real-time Listeners for Addons and Subscriptions
  useEffect(() => {
    let unsubAddons = () => { };
    try {
      unsubAddons = onSnapshot(
        collection(db, 'addons'),
        (snap) => {
          let fetchedAddons = [];
          if (snap && !snap.empty) {
            fetchedAddons = snap.docs
              .map((d) => ({ id: d.id, ...d.data() }))
              .filter((a) => a.is_active !== false);
          }
          setAddons(fetchedAddons);
        },
        (err) => console.warn('Addons snapshot warning:', err)
      );
    } catch (e) {
      console.warn('Addons listener setup error:', e);
    }

    const targetAccountId = credencials?.companyData?.id || credencials?.company;

    let unsubSubs = () => { };
    if (targetAccountId || activeUser) {
      try {
        const numId = Number(targetAccountId);
        const subCol = collection(db, 'subscriptions');
        unsubSubs = onSnapshot(
          subCol,
          (snap) => {
            if (snap && !snap.empty) {
              const matchedSubDoc = snap.docs.find((d) => {
                const data = d.data();
                return (
                  data.account_id === targetAccountId ||
                  data.account_id === String(targetAccountId) ||
                  (numId && data.production_company_id === numId) ||
                  (activeUser && data.owner_user_id === activeUser.uid)
                );
              });
              if (matchedSubDoc) {
                setSubscription({ id: matchedSubDoc.id, ...matchedSubDoc.data() });
              }
            }
          },
          (err) => console.warn('Subscriptions snapshot warning:', err)
        );
      } catch (e) {
        console.warn('Subscriptions listener init error:', e);
      }
    }

    return () => {
      if (typeof unsubAddons === 'function') unsubAddons();
      if (typeof unsubSubs === 'function') unsubSubs();
    };
  }, [credencials]);

  const fetchCompanyData = async () => {
    setLoading(true);
    try {
      const targetAccountId = credencials?.companyData?.id || credencials?.company;
      if (targetAccountId) {
        setAccountId(targetAccountId);
      }

      if (credencials?.companyData) {
        if (credencials.companyData.name) setCompanyName(credencials.companyData.name);
        if (credencials.companyData.pseudonimo || credencials.companyData.name) {
          setPseudonym(credencials.companyData.pseudonimo || credencials.companyData.name);
        }
        if (credencials.companyData.cnpj) setCpfCnpj(formatCpfCnpj(credencials.companyData.cnpj));
        if (credencials.companyData.whatsapp) setWhatsapp(formatPhone(credencials.companyData.whatsapp));
      }

      const apiUrl = getHubApiUrl();
      const headers = await getHubAuthHeaders(credencials);

      let fetchedData = null;

      if (targetAccountId || headers.Authorization) {
        const accountIdToUse = targetAccountId || 'me';
        try {
          const response = await axios.get(`${apiUrl}/accounts/${accountIdToUse}`, { headers });
          if (response && response.data) {
            fetchedData = response.data;
          }
        } catch (e1) {
          try {
            const responseMe = await axios.get(`${apiUrl}/accounts/me`, { headers });
            if (responseMe && responseMe.data) {
              fetchedData = responseMe.data;
            }
          } catch (e2) {
            console.warn('Hub API get account warning:', e2);
          }
        }
      }

      if (!fetchedData && targetAccountId) {
        try {
          const numId = Number(targetAccountId);
          let q = query(
            collection(db, 'accounts'),
            where('production_company_id', '==', isNaN(numId) ? targetAccountId : numId)
          );
          let snap = await getDocs(q);
          if ((!snap || snap.empty) && activeUser) {
            q = query(collection(db, 'accounts'), where('owner_user_id', '==', activeUser.uid));
            snap = await getDocs(q);
          }
          if (snap && !snap.empty) {
            fetchedData = snap.docs[0].data();
            fetchedData.id = snap.docs[0].id;
          }
        } catch (e) {
          console.warn('Firestore fallback warning:', e);
        }
      }

      if (fetchedData) {
        setAccountDocData(fetchedData);
        if (fetchedData.subscription) {
          setSubscription(fetchedData.subscription);
        }
        if (fetchedData.available_addons && fetchedData.available_addons.length > 0) {
          setAddons(fetchedData.available_addons);
        }
        if (fetchedData.id) setAccountId(fetchedData.id);
        if (fetchedData.name) setCompanyName(fetchedData.name);
        if (fetchedData.pseudonym || fetchedData.name)
          setPseudonym(fetchedData.pseudonym || fetchedData.name);
        if (fetchedData.segment || fetchedData.business_segment) {
          setSegment(fetchedData.segment || fetchedData.business_segment);
        } else if (credencials?.companyData?.segment) {
          setSegment(credencials.companyData.segment);
        }
        if (fetchedData.cpf_cnpj || fetchedData.cnpj)
          setCpfCnpj(formatCpfCnpj(fetchedData.cpf_cnpj || fetchedData.cnpj));
        if (fetchedData.whatsapp) setWhatsapp(formatPhone(fetchedData.whatsapp));
        if (fetchedData.logo_url || fetchedData.logoUrl)
          setLogoUrl(fetchedData.logo_url || fetchedData.logoUrl);
        if (fetchedData.responsible_name || fetchedData.responsibleName)
          setResponsibleName(fetchedData.responsible_name || fetchedData.responsibleName);
        if (fetchedData.postal_code || fetchedData.postalCode || fetchedData.cep) {
          setPostalCode(formatCep(fetchedData.postal_code || fetchedData.postalCode || fetchedData.cep));
        }
        if (fetchedData.address || fetchedData.street)
          setAddress(fetchedData.address || fetchedData.street);
        if (fetchedData.address_number || fetchedData.addressNumber || fetchedData.houseNumber) {
          setAddressNumber(
            fetchedData.address_number || fetchedData.addressNumber || fetchedData.houseNumber
          );
        }
        if (fetchedData.complement) setComplement(fetchedData.complement);
        if (fetchedData.province || fetchedData.neighborhood)
          setProvince(fetchedData.province || fetchedData.neighborhood);
        if (fetchedData.city) setCity(fetchedData.city);
        if (fetchedData.state) setState(fetchedData.state);
      }

      if (targetAccountId) {
        try {
          const invoicePromise = getInvoiceUrlForCompany(targetAccountId, credencials);
          if (invoicePromise && typeof invoicePromise.then === 'function') {
            invoicePromise
              .then((url) => {
                if (url) setPendingInvoiceUrl(url);
              })
              .catch(() => { });
          }
        } catch (e) { }
      }
    } catch (err) {
      console.error('Error fetching company configuration:', err);
    } finally {
      setLoading(false);
    }
  };

  // CNPJ Lookup via BrasilAPI
  const handleCnpjBlurOrChange = async (value) => {
    const cleanCnpj = (value || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    if (cleanCnpj.length === 14) {
      setCnpjLoading(true);
      try {
        const data = await fetchCnpjInfo(cleanCnpj);
        if (data) {
          if (data.razao_social) setCompanyName(data.razao_social);
          if (data.nome_fantasia || data.razao_social) {
            setPseudonym((prev) => prev || data.nome_fantasia || data.razao_social);
            setResponsibleName((prev) => prev || data.razao_social);
          }
          if (data.cep) setPostalCode(data.cep);
          if (data.logradouro) setAddress(data.logradouro);
          if (data.numero) setAddressNumber(data.numero);
          if (data.complemento) setComplement(data.complemento);
          if (data.bairro) setProvince(data.bairro);
          if (data.municipio) setCity(data.municipio);
          if (data.uf) setState(data.uf);

          setToast({
            open: true,
            message: 'Dados da empresa e endereço preenchidos via CNPJ!',
            severity: 'success',
          });
        }
      } catch (e) {
        setToast({
          open: true,
          message: 'CNPJ não encontrado na receita federal.',
          severity: 'warning',
        });
      } finally {
        setCnpjLoading(false);
      }
    }
  };

  // CEP Lookup via BrasilAPI
  const handleCepChange = async (value) => {
    const cleanCep = (value || '').replace(/\D/g, '');
    if (cleanCep.length === 8) {
      setCepLoading(true);
      try {
        const data = await fetchCepInfo(cleanCep);
        if (data) {
          if (data.street) setAddress(data.street);
          if (data.neighborhood) setProvince(data.neighborhood);
          if (data.city) setCity(data.city);
          if (data.state) setState(data.state);

          setToast({
            open: true,
            message: 'Endereço atualizado com sucesso via CEP!',
            severity: 'success',
          });
        }
      } catch (e) {
        setToast({
          open: true,
          message: 'CEP não encontrado.',
          severity: 'warning',
        });
      } finally {
        setCepLoading(false);
      }
    }
  };

  // Submit Handler (Company Data)
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setSaving(true);

    const updatePayload = {
      name: companyName,
      pseudonym: pseudonym,
      segment: segment,
      cpf_cnpj: cpfCnpj.replace(/[^a-zA-Z0-9]/g, '').toUpperCase(),
      whatsapp: whatsapp.replace(/\D/g, ''),
      logo_url: logoUrl,
      responsible_name: responsibleName,
      postal_code: postalCode.replace(/\D/g, ''),
      address,
      address_number: addressNumber,
      complement,
      province,
      city,
      state,
    };

    try {
      const apiUrl = getHubApiUrl();
      const headers = await getHubAuthHeaders(credencials);
      let savedSuccessfully = false;

      if (headers.Authorization) {
        try {
          const targetAccountId =
            accountId || credencials?.companyData?.id || credencials?.company || 'me';
          await axios.put(`${apiUrl}/accounts/${targetAccountId}`, updatePayload, {
            headers,
          });
          savedSuccessfully = true;
        } catch (err) {
          console.warn('Hub API update warning:', err);
        }
      }

      if (accountId) {
        try {
          const docRef = doc(db, 'accounts', String(accountId));
          await updateDoc(docRef, {
            ...updatePayload,
            updated_at: new Date(),
          });
          savedSuccessfully = true;
        } catch (err) {
          console.warn('Firestore update warning:', err);
        }
      }

      if (savedSuccessfully) {
        if (credencials?.companyData) {
          credencials.companyData.segment = segment;
        }
        if (typeof setCredencials === 'function') {
          setCredencials((prev) => ({
            ...prev,
            companyData: {
              ...(prev?.companyData || {}),
              segment,
            },
          }));
        }
        window.dispatchEvent(
          new CustomEvent('duzz:onboarding_seg_updated', {
            detail: {
              companyId: accountId || credencials?.companyData?.id || credencials?.company,
              segmentKey: segment,
              segment,
            },
          })
        );
        setToast({
          open: true,
          message: 'Configurações da empresa salvas com sucesso!',
          severity: 'success',
        });
      } else {
        throw new Error('Não foi possível salvar as alterações.');
      }
    } catch (err) {
      console.error('Save error:', err);
      setToast({
        open: true,
        message: err.message || 'Erro ao salvar configurações.',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  // Subscription action (Open invoice or Open checkout modal)
  const handleSubscriptionAction = async () => {
    if (pendingInvoiceUrl) {
      window.open(pendingInvoiceUrl, '_blank');
      return;
    }

    setLoadingInvoice(true);
    const newWindow = window.open('about:blank', '_blank');
    try {
      const targetCompanyId = accountId || credencials?.companyData?.id || credencials?.company;
      const url = await getInvoiceUrlForCompany(targetCompanyId, credencials);
      if (url) {
        if (newWindow && !newWindow.closed) {
          newWindow.location.href = url;
        } else {
          window.location.href = url;
        }
        setPendingInvoiceUrl(url);
      } else {
        if (newWindow) newWindow.close();
        setShowCheckoutModal(true);
      }
    } catch (e) {
      if (newWindow) newWindow.close();
      setShowCheckoutModal(true);
    } finally {
      setLoadingInvoice(false);
    }
  };

  // Toggle Add-on Handler
  const handleToggleAddon = async (addon, enable) => {
    const subId = subscription?.id || subscription?.subscription_id || accountDocData?.subscription?.id || accountDocData?.subscription?.subscription_id;
    if (!subId) {
      setShowCheckoutModal(true);
      return;
    }

    const currentIsTrial = Boolean(
      credencials?.companyData?.is_trial ||
      accountDocData?.is_trial ||
      subscription?.provider === 'trial' ||
      subscription?.status === 'trial'
    );

    setConfirmDialog({
      open: true,
      title: enable ? (currentIsTrial ? `Ativar ${addon.name} no Teste?` : `Contratar ${addon.name}?`) : `Desativar ${addon.name}?`,
      message: enable
        ? (currentIsTrial
          ? `O módulo "${addon.name}" será ativado gratuitamente durante o seu período de testes.`
          : `O módulo será ativado imediatamente e o valor de R$ ${Number(addon.amount || 0).toFixed(
            2
          )}/mês será incluído na sua assinatura Duzz.`)
        : `Deseja realmente remover o módulo ${addon.name} da sua assinatura?`,
      onConfirm: async () => {
        setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
        setToggleAddonLoading(true);
        try {
          const apiUrl = getHubApiUrl();
          const headers = await getHubAuthHeaders(credencials);

          const response = await axios.post(
            `${apiUrl}/subscriptions/${subId}/toggle-addon`,
            null,
            {
              params: {
                addon_id: addon.id,
                enable: enable,
              },
              headers,
            }
          );

          if (response.data) {
            const newAddonIds = response.data.addon_ids || (enable
              ? Array.from(new Set([...activeAddonIds, addon.id]))
              : activeAddonIds.filter((id) => id !== addon.id));

            setSubscription((prev) => (prev ? { ...prev, addon_ids: newAddonIds } : prev));
            setAccountDocData((prev) => (prev ? { ...prev, addon_ids: newAddonIds } : prev));

            setToast({
              open: true,
              message: enable
                ? (currentIsTrial
                  ? `Módulo ${addon.name} ativado gratuitamente no seu teste! 🎉`
                  : `Módulo ${addon.name} ativado com sucesso! 🎉`)
                : `Módulo ${addon.name} desativado.`,
              severity: 'success',
            });
          }
        } catch (err) {
          console.error('Toggle addon error:', err);
          const errorDetail = err?.response?.data?.detail;
          setToast({
            open: true,
            message:
              errorDetail ||
              'Não foi possível atualizar o módulo. Verifique sua assinatura.',
            severity: 'error',
          });
          if (!currentIsTrial && (!subscription?.id || err?.response?.status === 404)) {
            setShowCheckoutModal(true);
          }
        } finally {
          setToggleAddonLoading(false);
        }
      },
    });
  };

  // Submit Feedback Handler
  const handleSubmitFeedback = async (e) => {
    if (e) e.preventDefault();
    if (!feedbackTitle.trim() || !feedbackMessage.trim()) {
      setToast({
        open: true,
        message: 'Por favor, preencha o título e a descrição do seu feedback.',
        severity: 'warning',
      });
      return;
    }

    setSubmittingFeedback(true);
    try {
      const targetCompanyId = accountId || credencials?.companyData?.id || credencials?.company;
      const companyDisplayName = pseudonym || companyName || credencials?.companyData?.name || 'Empresa';

      const apiUrl = getHubApiUrl();
      const payload = {
        company_id: targetCompanyId || null,
        company_name: companyDisplayName,
        user_id: activeUser?.uid || String(credencials?.id || ''),
        user_email: activeUser?.email || credencials?.email || credencials?.username || '',
        category: feedbackCategory,
        rating: feedbackRating,
        title: feedbackTitle.trim(),
        message: feedbackMessage.trim(),
      };

      try {
        await axios.post(`${apiUrl}/accounts/feedback`, payload);
      } catch (apiErr) {
        // Fallback directly to Firestore if API call encounters network issue
        await addDoc(collection(db, 'feedbacks'), {
          ...payload,
          created_at: new Date(),
          status: 'NEW',
        });
      }

      setToast({
        open: true,
        message: 'Muito obrigado! Sua sugestão foi enviada para a equipe de produto do Duzz. 🚀',
        severity: 'success',
      });

      setFeedbackTitle('');
      setFeedbackMessage('');
      setFeedbackRating(5);
    } catch (err) {
      console.error('Feedback error:', err);
      setToast({
        open: true,
        message: 'Erro ao enviar feedback. Tente novamente mais tarde.',
        severity: 'error',
      });
    } finally {
      setSubmittingFeedback(false);
    }
  };

  // Active Add-on IDs resolution
  const activeAddonIds =
    subscription?.addon_ids ||
    (accountDocData?.addon_ids) ||
    (credencials?.companyData?.addon_ids) ||
    [];

  const contractedAddons = addons.filter((a) => activeAddonIds.includes(a.id));
  const availableAddons = addons.filter((a) => !activeAddonIds.includes(a.id));

  // Subscription calculation
  const isTrial =
    credencials?.companyData?.is_trial === true ||
    accountDocData?.is_trial === true ||
    subscription?.provider === 'trial' ||
    subscription?.status === 'trial';

  const subFrequency = String(subscription?.frequency || 'MONTHLY').toUpperCase();
  const isYearly = subFrequency === 'YEARLY' || subFrequency === 'ANNUAL' || subFrequency === 'YEAR';

  const contractedAddonsTotal = contractedAddons.reduce(
    (sum, a) => sum + Number(a.amount || 0),
    0
  );

  const basePlanAmount =
    subscription?.base_plan_amount !== undefined && subscription?.base_plan_amount !== null
      ? Number(subscription.base_plan_amount)
      : subscription?.amount !== undefined && subscription?.amount !== null
        ? Math.max(0, Number(subscription.amount) - contractedAddonsTotal)
        : null;

  const totalMonthlyAmount =
    subscription?.amount !== undefined && subscription?.amount !== null
      ? Number(subscription.amount)
      : basePlanAmount !== null
        ? basePlanAmount + contractedAddonsTotal
        : null;

  // Status mapping
  const getSubscriptionStatusInfo = () => {
    if (isTrial) {
      return {
        label: `Período de Testes (Trial) • ID para Login: ${credencials.companyData.id}`,
        color: '#2563eb',
        bgColor: '#eff6ff',
        borderColor: '#bfdbfe',
      };
    }
    const status = (subscription?.status || 'active').toLowerCase();
    if (status === 'active' || status === 'authorized') {
      return {
        label: `Assinatura Ativa • ID para Login: ${credencials.companyData.id}`,
        color: '#059669',
        bgColor: '#ecfdf5',
        borderColor: '#a7f3d0',
      };
    }
    if (status === 'pending_checkout' || status === 'pending') {
      return {
        label: `Aguardando Pagamento • ID para Login: ${credencials.companyData.id}`,
        color: '#d97706',
        bgColor: '#fffbeb',
        borderColor: '#fde68a',
      };
    }
    if (status === 'overdue') {
      return {
        label: `Fatura Vencida • ID para Login: ${credencials.companyData.id}`,
        color: '#dc2626',
        bgColor: '#fef2f2',
        borderColor: '#fecaca',
      };
    }
    return {
      label: `Licença Ativa • ID para Login: ${credencials.companyData.id}`,
      color: '#17497F',
      bgColor: '#f8fafc',
      borderColor: '#e2e8f0',
    };
  };

  const statusInfo = getSubscriptionStatusInfo();

  const renderSectionHeader = (Icon, title, description) => (
    <Box
      display="flex"
      alignItems="center"
      mb={2}
      style={{ borderLeft: '4px solid #17497F', paddingLeft: 10 }}
    >
      <Icon style={{ color: '#17497F', marginRight: 8, fontSize: 24 }} />
      <div>
        <Typography
          variant="subtitle1"
          style={{ fontWeight: 700, color: '#0f172a', lineHeight: 1.2, fontSize: '0.95rem' }}
        >
          {title}
        </Typography>
        {description && (
          <Typography variant="caption" style={{ color: '#64748b', fontSize: '0.78rem' }}>
            {description}
          </Typography>
        )}
      </div>
    </Box>
  );

  if (!isAdmin) {
    return <AccessRestricted requiredLevel="Administrador" />;
  }

  if (loading) {
    return (
      <PageContent>
        <UIContainer
          container
          direction="column"
          alignItems="center"
          justifyContent="center"
          style={{ padding: '80px 0' }}
        >
          <CircularProgress style={{ color: '#17497F' }} />
          <Typography variant="caption" style={{ marginTop: 14, color: '#64748b', fontWeight: 600 }}>
            Carregando configurações...
          </Typography>
        </UIContainer>
      </PageContent>
    );
  }

  return (
    <PageContent>
      <UIContainer container direction="column" style={{ width: '100%' }}>
        {/* ROW 1: TÍTULO DA PÁGINA */}
        <Grid item xs={12}>
          <UIFormTitle title="Configurações do Sistema" />
        </Grid>

        {/* ROW 2: CABEÇALHO DE IDENTIFICAÇÃO E AÇÕES */}
        <Grid item xs={12}>
          <div className={classes.headerBox}>
            {/* Lado Esquerdo / Centro: Identificação da Empresa */}
            <Box
              display="flex"
              flexDirection={isDesktop ? 'row' : 'column'}
              alignItems="center"
              textAlign={isDesktop ? 'left' : 'center'}
              style={{ gap: 14 }}
            >
              <Avatar
                src={logoUrl || ''}
                alt={pseudonym || companyName || 'Logo'}
                className={classes.companyAvatar}
              >
                <StorefrontIcon style={{ fontSize: 30, color: '#17497F' }} />
              </Avatar>
              <div>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent={isDesktop ? 'flex-start' : 'center'}
                  flexWrap="wrap"
                  style={{ gap: 6 }}
                >
                  <Typography
                    variant="subtitle1"
                    style={{
                      fontWeight: 800,
                      color: '#0f172a',
                      lineHeight: 1.2,
                      fontSize: '1.05rem',
                    }}
                  >
                    {pseudonym || companyName || 'Sua Empresa'}
                  </Typography>
                  <Chip
                    label="ADMINISTRADOR"
                    size="small"
                    style={{
                      backgroundColor: '#eff6ff',
                      color: '#17497F',
                      fontWeight: 700,
                      fontSize: 10,
                      height: 20,
                    }}
                  />
                  <Chip
                    label={statusInfo.label}
                    size="small"
                    style={{
                      backgroundColor: statusInfo.bgColor,
                      color: statusInfo.color,
                      border: `1px solid ${statusInfo.borderColor}`,
                      fontWeight: 700,
                      fontSize: 10,
                      height: 20,
                    }}
                  />
                </Box>
                <Typography
                  variant="caption"
                  style={{
                    color: '#64748b',
                    marginTop: 4,
                    display: 'block',
                    textAlign: isDesktop ? 'left' : 'center',
                  }}
                >
                  {cpfCnpj ? `CNPJ/CPF: ${cpfCnpj}` : 'Cadastro Comercial'}
                  {whatsapp ? ` • WhatsApp: ${whatsapp}` : ''}
                </Typography>
              </div>
            </Box>

            {/* Lado Direito / Centro: Botões de Ação */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent={isDesktop ? 'flex-end' : 'stretch'}
              width={isDesktop ? 'auto' : '100%'}
              flexDirection={isDesktop ? 'row' : 'column'}
              style={{ gap: 8 }}
            >
              <Button
                variant={pendingInvoiceUrl ? 'contained' : 'outlined'}
                color="primary"
                size="small"
                fullWidth={!isDesktop}
                onClick={handleSubscriptionAction}
                disabled={loadingInvoice}
                startIcon={
                  loadingInvoice ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : pendingInvoiceUrl ? (
                    <ReceiptIcon fontSize="small" />
                  ) : (
                    <PaymentIcon fontSize="small" />
                  )
                }
                style={{
                  borderRadius: 8,
                  padding: '8px 16px',
                  fontWeight: 700,
                  fontSize: '0.84rem',
                  textTransform: 'none',
                  borderColor: '#17497F',
                  backgroundColor: pendingInvoiceUrl ? '#17497F' : 'transparent',
                  color: pendingInvoiceUrl ? '#ffffff' : '#17497F',
                }}
              >
                {loadingInvoice
                  ? 'Buscando...'
                  : pendingInvoiceUrl
                    ? 'Abrir Fatura Asaas'
                    : 'Assinar / Renovar Licença'}
              </Button>

              {currentTab === 0 && (
                <Button
                  type="button"
                  onClick={handleSubmit}
                  variant="contained"
                  color="primary"
                  size="small"
                  fullWidth={!isDesktop}
                  disabled={saving}
                  startIcon={
                    saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon fontSize="small" />
                  }
                  style={{
                    backgroundColor: '#17497F',
                    borderRadius: 8,
                    padding: '8px 18px',
                    fontWeight: 700,
                    fontSize: '0.84rem',
                    textTransform: 'none',
                  }}
                >
                  {saving ? 'Salvando...' : 'Salvar Alterações'}
                </Button>
              )}
            </Box>
          </div>
        </Grid>

        {/* ROW 3: NAVEGAÇÃO ENTRE SEÇÕES (TABS NO DESKTOP / SELETOR DROPDOWN NO MOBILE) */}
        <Grid item xs={12}>
          {!isDesktop ? (
            <Box mb={2}>
              <FormControl variant="outlined" fullWidth size="small">
                <Select
                  value={currentTab}
                  onChange={(e) => setCurrentTab(Number(e.target.value))}
                  style={{
                    backgroundColor: '#f8fafc',
                    borderRadius: 10,
                    fontWeight: 700,
                    color: '#17497F',
                    fontSize: '0.9rem',
                  }}
                >
                  <MenuItem value={0}>
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                      <BusinessIcon style={{ fontSize: 18, color: '#17497F' }} />
                      <span>Dados da Empresa</span>
                    </Box>
                  </MenuItem>
                  <MenuItem value={1}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                        <ExtensionIcon style={{ fontSize: 18, color: '#17497F' }} />
                        <span>Plano & Add-ons</span>
                      </Box>
                      {contractedAddons.length > 0 && (
                        <Chip
                          label={`${contractedAddons.length} ativo`}
                          size="small"
                          style={{
                            height: 18,
                            fontSize: 9,
                            fontWeight: 700,
                            backgroundColor: '#ecfdf5',
                            color: '#059669',
                          }}
                        />
                      )}
                    </Box>
                  </MenuItem>
                  <MenuItem value={2}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                        <FeedbackIcon style={{ fontSize: 18, color: '#17497F' }} />
                        <span>Feedback & Sugestões</span>
                      </Box>
                      <Chip
                        label="Novo"
                        size="small"
                        style={{
                          height: 18,
                          fontSize: 9,
                          fontWeight: 700,
                          backgroundColor: '#eff6ff',
                          color: '#1d4ed8',
                        }}
                      />
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
          ) : (
            <div className={classes.tabsWrapper}>
              <Tabs
                value={currentTab}
                onChange={(e, val) => setCurrentTab(val)}
                variant="standard"
              >
                <Tab
                  icon={<BusinessIcon style={{ fontSize: 18 }} />}
                  label="Dados da Empresa"
                  className={classes.tabButton}
                />
                <Tab
                  icon={<ExtensionIcon style={{ fontSize: 18 }} />}
                  label={
                    <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                      <span>Plano & Add-ons</span>
                      {contractedAddons.length > 0 && (
                        <Chip
                          label={`${contractedAddons.length} ativo`}
                          size="small"
                          style={{
                            height: 18,
                            fontSize: 9,
                            fontWeight: 700,
                            backgroundColor: '#ecfdf5',
                            color: '#059669',
                          }}
                        />
                      )}
                    </Box>
                  }
                  className={classes.tabButton}
                />
                <Tab
                  icon={<FeedbackIcon style={{ fontSize: 18 }} />}
                  label={
                    <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                      <span>Feedback & Sugestões</span>
                      <Chip
                        label="Novo"
                        size="small"
                        style={{
                          height: 18,
                          fontSize: 9,
                          fontWeight: 700,
                          backgroundColor: '#eff6ff',
                          color: '#1d4ed8',
                        }}
                      />
                    </Box>
                  }
                  className={classes.tabButton}
                />
              </Tabs>
            </div>
          )}
        </Grid>

        {/* ROW 4: CONTEÚDO DA ABA SELECIONADA */}
        <Grid item xs={12}>
          {/* ============================================================== */}
          {/* ABA 0: DADOS DA EMPRESA                                        */}
          {/* ============================================================== */}
          {currentTab === 0 && (
            <form onSubmit={handleSubmit} style={{ width: '100%' }}>
              <Grid container spacing={2}>
                {/* Coluna 1: Marca & Identificação */}
                <Grid item xs={12} md={6}>
                  <div className={classes.formSectionBox}>
                    {renderSectionHeader(
                      StorefrontIcon,
                      'Marca & Identificação Comercial',
                      'Nome de exibição, WhatsApp e Logotipo'
                    )}

                    <Box mb={2}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        label="Nome Fantasia"
                        placeholder="Ex: Farmácia São José"
                        value={pseudonym}
                        onChange={(e) => setPseudonym(e.target.value)}
                        helperText="Nome que aparecerá no sistema, relatórios e catálogo online."
                      />
                    </Box>

                    <Box mb={2}>
                      <TextField
                        fullWidth
                        select
                        variant="outlined"
                        size="small"
                        label="Segmento do Negócio"
                        value={segment || 'Comércio Geral'}
                        onChange={(e) => setSegment(e.target.value)}
                        helperText="Define as missões de onboarding e recomendações inteligentes."
                      >
                        <MenuItem value="Roupas / Moda">Roupas & Moda (Vestuário, Calçados, Acessórios)</MenuItem>
                        <MenuItem value="Restaurante / Alimentação">Adega & Restaurante / Alimentação (Bares, Lanchonetes, Delivery)</MenuItem>
                        <MenuItem value="Cosméticos / Beleza">Cosméticos & Beleza (Salão, Barbearia, Estética)</MenuItem>
                        <MenuItem value="Mercado / Conveniência">Mercado & Conveniência (Mercearias, Hortifrúti)</MenuItem>
                        <MenuItem value="Serviços Gerais">Oficinas & Serviços Gerais (Manutenção, Prestadores)</MenuItem>
                        <MenuItem value="Comércio Geral">Comércio Geral (Outros ramos varejistas)</MenuItem>
                      </TextField>
                    </Box>

                    <Box mb={2}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        label="WhatsApp da Empresa *"
                        placeholder="(00) 00000-0000"
                        value={whatsapp}
                        onChange={(e) => setWhatsapp(formatPhone(e.target.value))}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PhoneIcon style={{ color: '#25D366', fontSize: 18 }} />
                            </InputAdornment>
                          ),
                        }}
                        helperText="Número para recebimento de vendas e suporte."
                      />
                    </Box>

                    <Divider style={{ margin: '16px 0' }} />

                    <Typography
                      variant="subtitle2"
                      style={{ fontWeight: 700, color: '#334155', marginBottom: 8, fontSize: '0.88rem' }}
                    >
                      Logotipo da Empresa
                    </Typography>
                    <UIImageUpload
                      folder="logos"
                      value={logoUrl}
                      onChange={(url) => setLogoUrl(url || '')}
                      maxImages={1}
                      label=""
                      hideSuggestions
                    />
                  </div>
                </Grid>

                {/* Coluna 2: Dados Fiscais & Endereço */}
                <Grid item xs={12} md={6}>
                  <div className={classes.formSectionBox}>
                    {renderSectionHeader(
                      AssignmentIndIcon,
                      'Dados Fiscais & Responsável',
                      'Preenchimento automático ao digitar CNPJ'
                    )}

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          size="small"
                          label="CPF / CNPJ *"
                          placeholder="Informe CPF ou CNPJ"
                          value={formatCpfCnpj(cpfCnpj)}
                          onChange={(e) => {
                            const raw = e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
                            setCpfCnpj(raw);
                            handleCnpjBlurOrChange(raw);
                          }}
                          helperText={
                            cnpjLoading
                              ? 'Buscando CNPJ na Receita...'
                              : 'Aceita CNPJ numérico e alfanumérico'
                          }
                          InputProps={{
                            endAdornment: cnpjLoading ? (
                              <CircularProgress size={16} />
                            ) : (
                              <SearchIcon style={{ color: '#94a3b8', fontSize: 18 }} />
                            ),
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          size="small"
                          label="Razão Social da Empresa *"
                          placeholder="Ex: João da Silva ME"
                          value={companyName}
                          onChange={(e) => setCompanyName(e.target.value)}
                        />
                      </Grid>
                    </Grid>

                    <Divider style={{ margin: '20px 0' }} />

                    {renderSectionHeader(
                      LocationOnIcon,
                      'Endereço Comercial & Faturamento',
                      'Preenchimento automático ao digitar CEP'
                    )}

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={5}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          size="small"
                          label="CEP *"
                          placeholder="00000-000"
                          value={postalCode}
                          onChange={(e) => {
                            const val = formatCep(e.target.value);
                            setPostalCode(val);
                            handleCepChange(val);
                          }}
                          helperText={cepLoading ? 'Buscando CEP...' : 'Preenchimento automático'}
                          InputProps={{
                            endAdornment: cepLoading ? <CircularProgress size={16} /> : null,
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={7}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          size="small"
                          label="Rua / Logradouro *"
                          placeholder="Ex: Av. Paulista"
                          value={address}
                          onChange={(e) => setAddress(e.target.value)}
                        />
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          size="small"
                          label="Número *"
                          placeholder="1000"
                          value={addressNumber}
                          onChange={(e) => setAddressNumber(e.target.value)}
                        />
                      </Grid>

                      <Grid item xs={12} sm={8}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          size="small"
                          label="Complemento"
                          placeholder="Sala 402"
                          value={complement}
                          onChange={(e) => setComplement(e.target.value)}
                        />
                      </Grid>

                      <Grid item xs={12} sm={5}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          size="small"
                          label="Bairro *"
                          placeholder="Centro"
                          value={province}
                          onChange={(e) => setProvince(e.target.value)}
                        />
                      </Grid>

                      <Grid item xs={12} sm={5}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          size="small"
                          label="Cidade *"
                          placeholder="São Paulo"
                          value={city}
                          onChange={(e) => setCity(e.target.value)}
                        />
                      </Grid>

                      <Grid item xs={12} sm={2}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          size="small"
                          label="UF *"
                          placeholder="SP"
                          inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
                          value={state}
                          onChange={(e) => setState(e.target.value.toUpperCase())}
                        />
                      </Grid>
                    </Grid>
                  </div>
                </Grid>

                {/* Botão de Salvar no Rodapé */}
                <Grid item xs={12}>
                  <Box display="flex" justifyContent={isDesktop ? 'flex-end' : 'stretch'} mt={1}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="medium"
                      fullWidth={!isDesktop}
                      disabled={saving}
                      startIcon={
                        saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />
                      }
                      style={{
                        backgroundColor: '#17497F',
                        borderRadius: 8,
                        padding: '10px 24px',
                        fontWeight: 700,
                        fontSize: '0.88rem',
                        textTransform: 'none',
                      }}
                    >
                      {saving ? 'Salvando...' : 'Salvar Alterações'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          )}

          {/* ============================================================== */}
          {/* ABA 1: PLANO & ADD-ONS                                         */}
          {/* ============================================================== */}
          {currentTab === 1 && (
            <Box width="100%">
              {/* 1. Resumo do Plano Atual Card */}
              <div className={classes.planCard}>
                <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                  <Grid item xs={12} md={7}>
                    <Box
                      display="flex"
                      flexDirection={isDesktop ? 'row' : 'column'}
                      alignItems={isDesktop ? 'flex-start' : 'center'}
                      textAlign={isDesktop ? 'left' : 'center'}
                      style={{ gap: 14 }}
                    >
                      <CardMembershipIcon style={{ fontSize: 38, color: '#17497F', marginTop: isDesktop ? 2 : 0 }} />
                      <div>
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent={isDesktop ? 'flex-start' : 'center'}
                          flexWrap="wrap"
                          style={{ gap: 6, marginBottom: 4 }}
                        >
                          <Typography variant="h6" style={{ fontWeight: 800, color: '#0f172a' }}>
                            {subscription?.plan_name ||
                              (isTrial ? 'Plano de Avaliação Gratuita' : 'Plano Comercial Essencial')}
                          </Typography>
                          <Chip
                            label={isYearly ? 'ANUAL' : 'MENSAL'}
                            size="small"
                            style={{
                              backgroundColor: '#eff6ff',
                              color: '#17497F',
                              fontWeight: 700,
                              fontSize: 10,
                              height: 20,
                            }}
                          />
                          <Chip
                            label={statusInfo.label}
                            size="small"
                            style={{
                              backgroundColor: statusInfo.bgColor,
                              color: statusInfo.color,
                              border: `1px solid ${statusInfo.borderColor}`,
                              fontWeight: 800,
                              fontSize: 10,
                              height: 20,
                            }}
                          />
                        </Box>
                        <Typography
                          variant="body2"
                          style={{
                            color: '#475569',
                            fontSize: '0.85rem',
                            maxWidth: 500,
                            margin: isDesktop ? 0 : '0 auto',
                          }}
                        >
                          {subscription?.description ||
                            'Acesso completo a vendas, estoque, clientes, caixa e relatórios comerciais.'}
                        </Typography>

                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent={isDesktop ? 'flex-start' : 'center'}
                          mt={1.5}
                          style={{ color: '#1e40af', fontSize: '0.8rem', fontWeight: 600 }}
                        >
                          <ScheduleIcon style={{ fontSize: 15, marginRight: 4 }} />
                          <span>
                            {isTrial
                              ? 'Período de avaliação com acesso integral aos recursos'
                              : 'Renovação contínua com garantia de suporte técnico'}
                          </span>
                        </Box>
                      </div>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={5}>
                    <Box
                      p={2}
                      borderRadius={12}
                      style={{
                        backgroundColor: '#ffffff',
                        border: '1px solid #cbd5e1',
                        boxShadow: '0 2px 8px rgba(15, 23, 42, 0.04)',
                      }}
                    >
                      <Typography variant="caption" style={{ color: '#17497F', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Resumo Financeiro da Licença
                      </Typography>

                      <Box display="flex" justifyContent="space-between" mt={1} mb={0.3}>
                        <Typography variant="body2" style={{ color: '#64748b', fontSize: '0.85rem' }}>
                          Plano Base:
                        </Typography>
                        <Typography variant="body2" style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.85rem' }}>
                          {basePlanAmount !== null
                            ? `R$ ${basePlanAmount.toFixed(2).replace('.', ',')} ${isYearly ? '/ ano' : '/ mês'}`
                            : isTrial
                              ? 'Grátis (Período de Testes)'
                              : 'R$ --'}
                        </Typography>
                      </Box>

                      {contractedAddons.length > 0 && (
                        <Box display="flex" justifyContent="space-between" mb={0.3}>
                          <Typography variant="body2" style={{ color: '#059669', fontSize: '0.85rem' }}>
                            + Add-ons Ativos ({contractedAddons.length}):
                          </Typography>
                          <Typography variant="body2" style={{ fontWeight: 700, color: '#059669', fontSize: '0.85rem' }}>
                            + R$ {contractedAddonsTotal.toFixed(2).replace('.', ',')} / mês
                          </Typography>
                        </Box>
                      )}

                      <Divider style={{ margin: '8px 0', backgroundColor: '#e2e8f0' }} />

                      <Box display="flex" justifyContent="space-between" alignItems="baseline">
                        <Typography variant="body1" style={{ fontWeight: 800, color: '#0f172a' }}>
                          Total:
                        </Typography>
                        <Typography variant="h6" style={{ fontWeight: 900, color: '#17497F' }}>
                          {totalMonthlyAmount !== null
                            ? `R$ ${totalMonthlyAmount.toFixed(2).replace('.', ',')}`
                            : isTrial && contractedAddonsTotal === 0
                              ? 'R$ 0,00'
                              : 'R$ --'}{' '}
                          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b' }}>
                            {isYearly ? '/ ano' : '/ mês'}
                          </span>
                        </Typography>
                      </Box>

                      <Box mt={1.5} display="flex" style={{ gap: 8 }}>
                        <Button
                          fullWidth
                          variant="contained"
                          size="small"
                          onClick={() => setShowCheckoutModal(true)}
                          style={{
                            backgroundColor: '#17497F',
                            color: '#ffffff',
                            fontWeight: 700,
                            fontSize: '0.8rem',
                            textTransform: 'none',
                            borderRadius: 8,
                            padding: '6px 12px',
                          }}
                        >
                          Alterar / Renovar Plano
                        </Button>
                        {pendingInvoiceUrl && (
                          <Tooltip title="Abrir link de pagamento da fatura">
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => window.open(pendingInvoiceUrl, '_blank')}
                              style={{
                                borderColor: '#17497F',
                                color: '#17497F',
                                borderRadius: 8,
                                minWidth: 38,
                                padding: '4px 8px',
                              }}
                            >
                              <OpenInNewIcon fontSize="small" />
                            </Button>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </div>

              {/* 2. Add-ons Contratados (Já Ativos) */}
              <Box mb={3.5}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                  <div>
                    <Typography variant="subtitle1" style={{ fontWeight: 800, color: '#0f172a' }}>
                      Recursos & Add-ons Contratados
                    </Typography>
                    <Typography variant="caption" style={{ color: '#64748b' }}>
                      Módulos adicionais ativos na sua conta comercial.
                    </Typography>
                  </div>
                  <Chip
                    icon={<CheckCircleIcon style={{ color: '#059669', fontSize: 15 }} />}
                    label={`${contractedAddons.length} Contratado${contractedAddons.length !== 1 ? 's' : ''
                      }`}
                    size="small"
                    style={{
                      backgroundColor: '#ecfdf5',
                      color: '#059669',
                      fontWeight: 700,
                      border: '1px solid #a7f3d0',
                    }}
                  />
                </Box>

                {contractedAddons.length > 0 ? (
                  <Grid container spacing={2}>
                    {contractedAddons.map((addon) => (
                      <Grid item xs={12} sm={6} key={addon.id}>
                        <Card elevation={0} className={classes.addonCardActive}>
                          <CardContent style={{ padding: 18 }}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                              <Box display="flex" alignItems="center" style={{ gap: 10 }}>
                                <Avatar style={{ backgroundColor: '#ecfdf5', color: '#059669', width: 38, height: 38 }}>
                                  {addon.id === 'catalog' ? (
                                    <StorefrontIcon style={{ fontSize: 20 }} />
                                  ) : (
                                    <ExtensionIcon style={{ fontSize: 20 }} />
                                  )}
                                </Avatar>
                                <div>
                                  <Typography variant="subtitle2" style={{ fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
                                    {addon.name}
                                  </Typography>
                                  <Typography variant="caption" style={{ color: '#059669', fontWeight: 700 }}>
                                    R$ {Number(addon.amount || 0).toFixed(2).replace('.', ',')} / mês
                                  </Typography>
                                </div>
                              </Box>
                              <Chip
                                label="ATIVO"
                                size="small"
                                style={{
                                  backgroundColor: '#10b981',
                                  color: '#ffffff',
                                  fontWeight: 800,
                                  fontSize: 9,
                                  height: 20,
                                }}
                              />
                            </Box>

                            <Typography variant="body2" style={{ color: '#475569', margin: '8px 0 12px 0', fontSize: '0.84rem' }}>
                              {addon.description}
                            </Typography>

                            {addon.features && addon.features.length > 0 && (
                              <Box mt={0.5}>
                                {addon.features.map((feat, idx) => (
                                  <Box key={idx} display="flex" alignItems="flex-start" mb={0.5} style={{ gap: 6 }}>
                                    <CheckCircleIcon style={{ color: '#10b981', fontSize: 14, marginTop: 2, flexShrink: 0 }} />
                                    <Typography variant="caption" style={{ color: '#334155', fontWeight: 600, fontSize: '0.78rem' }}>
                                      {feat}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            )}
                          </CardContent>

                          <Divider style={{ margin: '0 16px' }} />

                          <CardActions style={{ padding: '10px 16px', justifyContent: 'space-between' }}>
                            {addon.id === 'catalog' ? (
                              <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                onClick={() => history.push('/home/gestao/catalogo')}
                                endIcon={<LaunchIcon fontSize="small" />}
                                style={{
                                  textTransform: 'none',
                                  fontWeight: 700,
                                  fontSize: '0.78rem',
                                  borderRadius: 6,
                                  borderColor: '#17497F',
                                  color: '#17497F',
                                  padding: '3px 10px',
                                }}
                              >
                                Configurar Catálogo
                              </Button>
                            ) : (
                              <Box />
                            )}

                            <Button
                              size="small"
                              onClick={() => handleToggleAddon(addon, false)}
                              disabled={toggleAddonLoading}
                              style={{
                                textTransform: 'none',
                                color: '#94a3b8',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                              }}
                            >
                              Desativar
                            </Button>
                          </CardActions>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Box
                    p={3}
                    textAlign="center"
                    borderRadius={10}
                    style={{
                      border: '1px dashed #cbd5e1',
                      backgroundColor: '#f8fafc',
                    }}
                  >
                    <ExtensionIcon style={{ fontSize: 36, color: '#94a3b8', marginBottom: 4 }} />
                    <Typography variant="body2" style={{ fontWeight: 700, color: '#334155' }}>
                      Nenhum add-on contratado no momento
                    </Typography>
                    <Typography variant="caption" style={{ color: '#64748b', display: 'block', marginTop: 2 }}>
                      Você possui o plano comercial ativo. Confira abaixo os módulos disponíveis para potencializar suas vendas.
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* 3. Add-ons Disponíveis para Upgrade */}
              <Box mb={2}>
                <Box mb={1.5}>
                  <Typography variant="subtitle1" style={{ fontWeight: 800, color: '#0f172a' }}>
                    Add-ons Disponíveis para Contratação
                  </Typography>
                  <Typography variant="caption" style={{ color: '#64748b' }}>
                    Recursos opcionais para expandir as funcionalidades da sua loja.
                  </Typography>
                </Box>

                {availableAddons.length > 0 ? (
                  <Grid container spacing={2}>
                    {availableAddons.map((addon) => (
                      <Grid item xs={12} sm={6} key={addon.id}>
                        <Card elevation={0} className={classes.addonCardAvailable}>
                          <CardContent style={{ padding: 18 }}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                              <Box display="flex" alignItems="center" style={{ gap: 10 }}>
                                <Avatar style={{ backgroundColor: '#eff6ff', color: '#17497F', width: 38, height: 38 }}>
                                  {addon.id === 'catalog' ? (
                                    <StorefrontIcon style={{ fontSize: 20 }} />
                                  ) : (
                                    <FlashOnIcon style={{ fontSize: 20 }} />
                                  )}
                                </Avatar>
                                <div>
                                  <Typography variant="subtitle2" style={{ fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
                                    {addon.name}
                                  </Typography>
                                  <Typography variant="caption" style={{ color: '#17497F', fontWeight: 800 }}>
                                    + R$ {Number(addon.amount || 0).toFixed(2).replace('.', ',')} / mês
                                  </Typography>
                                </div>
                              </Box>
                              <Chip
                                label="DISPONÍVEL"
                                size="small"
                                style={{
                                  backgroundColor: '#eff6ff',
                                  color: '#17497F',
                                  fontWeight: 700,
                                  fontSize: 9,
                                  height: 20,
                                }}
                              />
                            </Box>

                            <Typography variant="body2" style={{ color: '#475569', margin: '8px 0 12px 0', fontSize: '0.84rem' }}>
                              {addon.description}
                            </Typography>

                            {addon.features && addon.features.length > 0 && (
                              <Box mt={0.5}>
                                {addon.features.map((feat, idx) => (
                                  <Box key={idx} display="flex" alignItems="flex-start" mb={0.5} style={{ gap: 6 }}>
                                    <CheckCircleOutlineIcon style={{ color: '#17497F', fontSize: 14, marginTop: 2, flexShrink: 0 }} />
                                    <Typography variant="caption" style={{ color: '#334155', fontWeight: 600, fontSize: '0.78rem' }}>
                                      {feat}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            )}
                          </CardContent>

                          <Divider style={{ margin: '0 16px' }} />

                          <CardActions style={{ padding: '10px 16px', justifyContent: 'flex-end' }}>
                            <Button
                              variant="contained"
                              color="primary"
                              size="small"
                              onClick={() => handleToggleAddon(addon, true)}
                              disabled={toggleAddonLoading}
                              startIcon={<AddCircleOutlineIcon fontSize="small" />}
                              style={{
                                backgroundColor: '#17497F',
                                borderRadius: 6,
                                padding: '5px 14px',
                                fontWeight: 700,
                                fontSize: '0.78rem',
                                textTransform: 'none',
                              }}
                            >
                              + Contratar Add-on
                            </Button>
                          </CardActions>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Box
                    p={2}
                    textAlign="center"
                    borderRadius={10}
                    style={{
                      border: '1px solid #e2e8f0',
                      backgroundColor: '#f8fafc',
                    }}
                  >
                    <Typography variant="body2" style={{ color: '#64748b' }}>
                      Sua empresa já contratou todos os módulos adicionais disponíveis. 🎉
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* ============================================================== */}
          {/* ABA 2: FEEDBACK & SUGESTÕES                                    */}
          {/* ============================================================== */}
          {currentTab === 2 && (
            <div className={classes.feedbackContainer}>
              <Box mb={2.5} textAlign="center">
                <Avatar
                  style={{
                    backgroundColor: '#eff6ff',
                    color: '#17497F',
                    width: 48,
                    height: 48,
                    margin: '0 auto 8px auto',
                  }}
                >
                  <EmojiObjectsIcon style={{ fontSize: 26 }} />
                </Avatar>
                <Typography variant="h6" style={{ fontWeight: 800, color: '#0f172a' }}>
                  Central de Feedback & Sugestões
                </Typography>
                <Typography variant="body2" style={{ color: '#64748b', maxWidth: 580, margin: '4px auto 0 auto', fontSize: '0.85rem' }}>
                  Envie sugestões de novos recursos, relate dificuldades ou compartilhe sua experiência diretamente com a equipe de produto do Duzz!
                </Typography>
              </Box>

              <Divider style={{ margin: '16px 0' }} />

              <form onSubmit={handleSubmitFeedback}>
                <Grid container spacing={2}>
                  {/* Categoria */}
                  <Grid item xs={12}>
                    <Typography variant="caption" style={{ fontWeight: 700, color: '#334155', display: 'block', marginBottom: 8 }}>
                      Tipo de Mensagem:
                    </Typography>
                    <Grid container spacing={1}>
                      {[
                        { id: 'SUGGESTION', label: '💡 Sugestão de Recurso', color: '#17497F' },
                        { id: 'USABILITY', label: '⚡ Melhoria de Usabilidade', color: '#059669' },
                        { id: 'ISSUE', label: '🔧 Relatar Dificuldade', color: '#d97706' },
                        { id: 'PRAISE', label: '❤️ Elogio & Experiência', color: '#e11d48' },
                      ].map((cat) => (
                        <Grid item xs={12} sm={6} md={3} key={cat.id}>
                          <Box
                            onClick={() => setFeedbackCategory(cat.id)}
                            p={1.2}
                            textAlign="center"
                            borderRadius={8}
                            style={{
                              cursor: 'pointer',
                              border:
                                feedbackCategory === cat.id
                                  ? `2px solid ${cat.color}`
                                  : '1px solid #cbd5e1',
                              backgroundColor:
                                feedbackCategory === cat.id ? '#ffffff' : '#f8fafc',
                              fontWeight: feedbackCategory === cat.id ? 700 : 500,
                              color: feedbackCategory === cat.id ? cat.color : '#475569',
                              transition: 'all 0.15s ease',
                            }}
                          >
                            <Typography variant="body2" style={{ fontWeight: 'inherit', color: 'inherit', fontSize: '0.82rem' }}>
                              {cat.label}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>

                  {/* Rating */}
                  <Grid item xs={12}>
                    <Box display="flex" flexDirection="column" alignItems="center" p={1.5} bgcolor="#ffffff" borderRadius={8} border="1px solid #e2e8f0">
                      <Typography variant="caption" style={{ fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                        Como você avalia sua experiência com o sistema?
                      </Typography>
                      <Rating
                        name="feedback-rating"
                        value={feedbackRating}
                        onChange={(e, val) => setFeedbackRating(val || 5)}
                        size="medium"
                      />
                    </Box>
                  </Grid>

                  {/* Título */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      label="Título / Resumo da Sugestão *"
                      placeholder="Ex: Adicionar filtro por vendedor nas vendas"
                      value={feedbackTitle}
                      onChange={(e) => setFeedbackTitle(e.target.value)}
                    />
                  </Grid>

                  {/* Descrição */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      variant="outlined"
                      size="small"
                      label="Detalhes da sua Sugestão ou Experiência *"
                      placeholder="Descreva o que gostaria de ver no sistema..."
                      value={feedbackMessage}
                      onChange={(e) => setFeedbackMessage(e.target.value)}
                    />
                  </Grid>

                  {/* Submit button */}
                  <Grid item xs={12}>
                    <Box display="flex" justifyContent={isDesktop ? 'flex-end' : 'stretch'} mt={1}>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        size="medium"
                        fullWidth={!isDesktop}
                        disabled={submittingFeedback}
                        startIcon={
                          submittingFeedback ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <SendIcon fontSize="small" />
                          )
                        }
                        style={{
                          backgroundColor: '#17497F',
                          borderRadius: 8,
                          padding: '10px 24px',
                          fontWeight: 700,
                          fontSize: '0.88rem',
                          textTransform: 'none',
                        }}
                      >
                        {submittingFeedback ? 'Enviando...' : 'Enviar Sugestão'}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </form>
            </div>
          )}
        </Grid>
      </UIContainer>

      {/* Subscription Checkout Modal */}
      <SubscriptionCheckoutModal
        open={showCheckoutModal}
        onClose={() => setShowCheckoutModal(false)}
      />

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, title: '', message: '', onConfirm: null })}
      >
        <DialogTitle style={{ fontWeight: 700 }}>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmDialog.message}</DialogContentText>
        </DialogContent>
        <DialogActions style={{ padding: '12px 20px' }}>
          <Button
            onClick={() => setConfirmDialog({ open: false, title: '', message: '', onConfirm: null })}
            color="default"
            style={{ textTransform: 'none', fontWeight: 600 }}
          >
            Cancelar
          </Button>
          <Button
            onClick={confirmDialog.onConfirm}
            color="primary"
            variant="contained"
            style={{
              backgroundColor: '#17497F',
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 6,
            }}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast Notification */}
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast({ ...toast, open: false })}
      >
        <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>
          {toast.message}
        </Alert>
      </Snackbar>
    </PageContent>
  );
};

export default ConfiguracaoEmpresa;
